import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BrandProfileInputDto, BrandVoiceInputDto, Platform } from "@marketing/shared";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";

export type StoredVoice = BrandVoiceInputDto & {
  id: string;
  socialAccountId: string | null;
  socialAccount: { id: string; platform: Platform; displayName: string } | null;
};

export type GeneratedVariant = {
  platform: Platform;
  caption: string;
  hashtags: string[];
};

export type CalendarVariant = {
  id: string;
  caption: string;
  hashtags: string[];
  status: string;
  permalink: string | null;
  errorMessage: string | null;
  publishedAt: string | null;
  socialAccount: { id: string; platform: Platform; displayName: string };
};

export type CalendarPost = {
  id: string;
  topic: string | null;
  origin: string;
  status: string;
  scheduledAt: string | null;
  createdAt: string;
  regenerationCount: number;
  brand: { id: string; name: string; timezone: string };
  mediaAsset: { originalUrl: string; enhancedUrl: string | null } | null;
  variants: CalendarVariant[];
};

export type Calendar = {
  window: { from: string; to: string };
  scheduled: CalendarPost[];
  unscheduled: CalendarPost[];
  counts: Record<string, number>;
  /** True when the window held more posts than one request returns. */
  truncated: boolean;
  limit: number;
};

/** The brand fact sheet. One row per brand, no per-page override. */
export function useProfile(brandId: string | null) {
  return useQuery({
    queryKey: ["profile", brandId],
    queryFn: () =>
      unwrap<BrandProfileInputDto | null>(api.get(`/content/brands/${brandId}/profile`)),
    enabled: !!brandId,
  });
}

export function useSaveProfile(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BrandProfileInputDto) =>
      unwrap<BrandProfileInputDto>(api.put(`/content/brands/${brandId}/profile`, input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["profile", brandId] });
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useVoices(brandId: string | null) {
  return useQuery({
    queryKey: ["voices", brandId],
    queryFn: () => unwrap<StoredVoice[]>(api.get(`/content/brands/${brandId}/voices`)),
    enabled: !!brandId,
  });
}

export function useSaveVoice(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BrandVoiceInputDto) =>
      unwrap<StoredVoice>(api.put(`/content/brands/${brandId}/voices`, input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["voices", brandId] });
      // The sidebar derives "what is next" from the brands list counts, so a
      // completed step has to refresh them or the NEXT tag stays put.
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

/** Generates against unsaved edits, so the owner can hear the voice first. */
export function usePreviewVoice() {
  return useMutation({
    mutationFn: (args: {
      brandId: string;
      voice?: Partial<BrandVoiceInputDto>;
      topic?: string;
    }) =>
      unwrap<{ variants: GeneratedVariant[] }>(api.post("/content/voice/preview", args)),
  });
}

/**
 * The calendar for a brand, over a window.
 *
 * The window is part of the query key, so changing the range refetches rather
 * than re-filtering a cached month that may not contain what is being looked
 * for. An absent bound is omitted from the request entirely — the API reads
 * that as unbounded on that side, which is what "upcoming" and "all" need.
 */
export function useCalendar(
  brandId: string | null,
  window: { from?: string; to?: string } = {},
) {
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useQuery({
    queryKey: ["calendar", workspaceId, brandId, window.from ?? null, window.to ?? null],
    queryFn: () =>
      unwrap<Calendar>(
        api.get("/content/calendar", {
          params: {
            ...(brandId ? { brandId } : {}),
            ...(window.from ? { from: window.from } : {}),
            ...(window.to ? { to: window.to } : {}),
          },
        }),
      ),
    enabled: !!workspaceId,
    // The previous window stays on screen while the next one loads, so
    // changing the range does not blank the page.
    placeholderData: (previous) => previous,
  });
}

export function useGeneratePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      brandId: string;
      topic: string;
      facts?: string[];
      withImage?: boolean;
    }) => unwrap<{ imageError: string | null }>(api.post("/content/posts/generate", args)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["calendar"] });
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useRegeneratePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) =>
      unwrap<{ regenerationsLeft: number }>(api.post(`/content/posts/${postId}/regenerate`, {})),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["calendar"] });
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

/**
 * A picture for a post that already has its words.
 *
 * Not the same thing as regenerating, and the difference matters to whoever is
 * clicking: regenerating rewrites the captions, which may already have been
 * read and approved. This leaves every word alone and only fills in the
 * missing image.
 */
export function useGeneratePostImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) =>
      unwrap<{
        mediaAsset: { originalUrl: string; enhancedUrl: string | null } | null;
        featuredProductId: string | null;
      }>(api.post(`/content/posts/${postId}/image`, {})),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["calendar"] });
      void qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });
}
